import { createEvent, createStore, sample } from 'effector'
import { fx, Response, ResponseError } from '../../../libs/request'
import { TxidCheckFormData } from './widgets/TxidCheckForm'
import { AddressCheckFormData } from './widgets/AddressCheckForm'
import {
  AmlCheckResponse,
  CheckAddressRequest,
  CheckAddressResponse,
  CheckTransactionHashRequest,
  CheckTransactionHashResponse,
} from './types'

const errorMessages: Record<string, string> = {
  INVALID_TRANSACTION_HASH: 'Некорректный формат хеша транзакции',
  TRANSACTION_NOT_FOUND: 'Транзакция не найдена',
  EXTERNAL_SERVICE_UNAVAILABLE:
    'Сервис блокчейна или AML-проверки недоступен, попробуйте позже',
  AML_CHECK_NOT_FOUND: 'AML-проверка не найдена',
}

type CheckTransactionHashError = Response<
  ResponseError<CheckTransactionHashRequest>
>

type CheckAddressError = Response<ResponseError<CheckAddressRequest>>

// Бэк wallet-guard присылает код ошибки строкой, а общий тип ResponseError описывает его числом.
const getErrorCode = <Payload>(response: Response<ResponseError<Payload>>) =>
  String(response.body.code)

const getErrorMessage = (response: CheckTransactionHashError) => {
  if (response.status === 403) {
    return 'Нет прав на проверку транзакций'
  }

  return (
    errorMessages[getErrorCode(response)] ??
    response.body.message ??
    'Не удалось проверить транзакцию'
  )
}

const getAddressErrorMessage = (response: CheckAddressError) => {
  return (
    errorMessages[getErrorCode(response)] ??
    response.body.message ??
    'Не удалось проверить адрес'
  )
}

export const transactionHashCheckSubmitted = createEvent<TxidCheckFormData>()

export const amlCheckRefreshRequested =
  createEvent<CheckTransactionHashResponse>()

export const checkTransactionHashFx = fx<
  CheckTransactionHashRequest,
  CheckTransactionHashResponse
>({
  method: 'POST',
  url: '/wallet-guard/verify/transaction',
})

// Тот же запрос, но с amlCheckId: бэк возвращает текущее состояние AML-проверки.
export const refreshAmlCheckFx = fx<
  CheckTransactionHashRequest,
  AmlCheckResponse
>({
  method: 'POST',
  url: '/wallet-guard/verify/transaction',
})

export const $checkTransactionHashResult =
  createStore<CheckTransactionHashResponse | null>(null)
    .on(checkTransactionHashFx.doneData, (_, response) => response.body)
    .on(
      refreshAmlCheckFx.doneData,
      (currentResult, response) =>
        currentResult && { ...currentResult, aml: response.body.aml },
    )
    .reset(checkTransactionHashFx)

export const $errorMessage = createStore<string | null>(null)
  .on(checkTransactionHashFx.failData, (_, response) =>
    getErrorMessage(response),
  )
  .on(refreshAmlCheckFx.failData, (_, response) => getErrorMessage(response))
  .reset(checkTransactionHashFx, refreshAmlCheckFx.done)

sample({
  clock: transactionHashCheckSubmitted,
  fn: ({ network, currency, txid }) => ({
    body: {
      network: network.trim(),
      currency: currency.trim(),
      txHash: txid.trim(),
    },
  }),
  target: checkTransactionHashFx,
})

sample({
  clock: amlCheckRefreshRequested,
  fn: ({ transaction, aml }) => ({
    body: {
      network: transaction.network,
      currency: transaction.currency,
      txHash: transaction.hash,
      amlCheckId: aml.checkId,
    },
  }),
  target: refreshAmlCheckFx,
})

export const addressCheckSubmitted = createEvent<AddressCheckFormData>()

export const addressCheckRefreshRequested = createEvent<CheckAddressResponse>()

export const checkAddressFx = fx<CheckAddressRequest, CheckAddressResponse>({
  method: 'POST',
  url: '/wallet-guard/verify/address',
})

// Тот же запрос, но с amlCheckId: бэк возвращает текущее состояние проверки адреса.
export const refreshAddressCheckFx = fx<
  CheckAddressRequest,
  CheckAddressResponse
>({
  method: 'POST',
  url: '/wallet-guard/verify/address',
})

export const $checkAddressResult = createStore<CheckAddressResponse | null>(
  null,
)
  .on(checkAddressFx.doneData, (_, response) => response.body)
  .on(refreshAddressCheckFx.doneData, (_, response) => response.body)
  .reset(checkAddressFx)

export const $addressErrorMessage = createStore<string | null>(null)
  .on(checkAddressFx.failData, (_, response) =>
    getAddressErrorMessage(response),
  )
  .on(refreshAddressCheckFx.failData, (_, response) =>
    getAddressErrorMessage(response),
  )
  .reset(checkAddressFx, refreshAddressCheckFx.done)

sample({
  clock: addressCheckSubmitted,
  fn: ({ network, walletAddress }) => ({
    body: {
      network,
      address: walletAddress.trim(),
    },
  }),
  target: checkAddressFx,
})

sample({
  clock: addressCheckRefreshRequested,
  fn: ({ checkId, subject }) => ({
    body: {
      network: subject.network,
      address: subject.address,
      amlCheckId: checkId,
    },
  }),
  target: refreshAddressCheckFx,
})
